// Version: 2.4
import React, { useEffect, useState } from 'react';
import { authFetch } from './auth';

// Resolve framework key to display label and colour — matched by substring
// so it works regardless of how CISO Assistant or .env names the framework.
// Identity colours — used for card border/ring only, never for RAG status
// Must be distinct and must NOT be red, amber or green
const FW_IDENTITY = {
  'cyber':  '#0a84ff',  // magenta
  'gdpr':   '#bf5af2',  // purple
  'iso':    '#5856d6',  // indigo — distinct from status colours
};

function resolveFw(key) {
  const k = (key || '').toLowerCase();
  if (k.includes('cyber essentials'))
    return { label: 'Cyber Essentials', identity: FW_IDENTITY.cyber };
  if (k.includes('general data protection') || k.includes('gdpr'))
    return { label: 'General Data Protection Regulation', identity: FW_IDENTITY.gdpr };
  if (k.includes('iso 27001') || k.includes('iso/iec 27001') || k.includes('iso27001'))
    return { label: 'ISO 27001', identity: FW_IDENTITY.iso };
  return { label: key, identity: '#86868b' };
}

// RAG colour for percentage — based purely on progress
function ragColor(pct) {
  if (pct === 0)   return '#86868b';  // grey — not started
  if (pct < 50)    return '#ff2d55';  // red
  if (pct < 80)    return '#e6b800';  // amber
  return '#30d158';                   // green
}

function pct(val, total) {
  if (!total) return 0;
  return Math.round((val / total) * 100);
}

function ProgressRing({ percent, color, size = 52 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (percent / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border)" strokeWidth={6} />
      <circle
        cx={size/2} cy={size/2} r={r}
        fill="none"
        stroke={color}
        strokeWidth={6}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
    </svg>
  );
}

function FrameworkCard({ fw, data, index }) {
  const { label, identity } = resolveFw(fw);
  const reqs_ = data?.requirements || {};
  const pct_  = reqs_?.percent_compliant || 0;
  const ragCol = ragColor(pct_);

  if (!data || data.not_configured) {
    return (
      <div style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '16px 18px',
        opacity: 0.5,
        minWidth: 0,
      }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Not configured</div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginTop: 6 }}>{label}</div>
      </div>
    );
  }

  const reqs        = data.requirements || {};
  const total       = reqs.total_assessable || 0;
  const compliant   = reqs.compliant || 0;
  const nonComp     = reqs.non_compliant || 0;
  const inProg      = reqs.in_progress || 0;
  const notAss      = Math.max(0, total - compliant - nonComp - inProg);
  const pcCompliant = reqs.percent_compliant || 0;

  return (
    <div style={{
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderTop: `3px solid ${identity}`,
      borderRadius: 10,
      padding: '16px 18px',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      minWidth: 0,
    }}>
      {/* Top section — fixed min-height so all cards align regardless of label length */}
      <div style={{ minHeight: 80, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ProgressRing percent={pcCompliant} color={ragCol} size={52} />
          <div>
            <div style={{ fontSize: 26, fontWeight: 800, color: ragCol, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
              {pcCompliant}%
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>compliant</div>
          </div>
        </div>
        {/* Label below the ring — wraps freely without pushing other cards */}
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', lineHeight: 1.3 }}>
          {label}
        </div>
      </div>

      {/* Requirement counts */}
      {total > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
          {[
            { label: 'Compliant',    val: compliant, color: '#30d158' },
            { label: 'Non-comp',     val: nonComp,   color: '#ff2d55' },
            { label: 'In progress',  val: inProg,    color: '#e6b800' },
            { label: 'Not assessed', val: notAss,    color: 'var(--text-muted)' },
          ].map(({ label: l, val, color: c }) => (
            <div key={l} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: c, fontFamily: 'var(--font-mono)' }}>{val}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.2 }}>{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Progress bar */}
      {total > 0 && (
        <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${pct(compliant, total)}%`,
            background: ragCol,
            borderRadius: 2,
            transition: 'width 0.6s ease',
          }} />
        </div>
      )}

      {/* Footer */}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
        <span>{total} assessable requirement{total !== 1 ? 's' : ''}</span>
        {data.updated_at && (
          <span>Updated {new Date(data.updated_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT'}</span>
        )}
      </div>
    </div>
  );
}

export default function ComplianceTile({ slug }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    authFetch(`/api/clients/${slug}/compliance`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Could not load compliance data'); setLoading(false); });
  }, [slug]);

  if (loading) return (
    <div className="card" style={{ padding: 24, textAlign: 'center' }}>
      <span className="spinner" />
    </div>
  );

  if (error || !data?.available) return null;

  const posture    = data.data?.posture || {};
  const frameworks = data.data?.frameworks || Object.keys(posture);
  const pushedAt   = data.pushed_at;

  if (frameworks.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 14,
        flexWrap: 'wrap',
        gap: 8,
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          Compliance Posture
        </div>
        {pushedAt && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Last updated {new Date(pushedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT'}
          </div>
        )}
      </div>
      <div className="compliance-fw-grid">
        {frameworks.map((fw, i) => (
          <FrameworkCard
            key={fw}
            fw={fw}
            data={posture[fw]}
            index={i}
          />
        ))}
      </div>
      <style>{`
        .compliance-fw-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        @media (max-width: 700px) {
          .compliance-fw-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}